<template>
  <div class="card mb-4">
    <div class="card-body pb-3 d-flex flex-wrap">
      <oms-filter-plans
        :title="t('features.vue.orders.filters.plan')"
        :plan-types="planTypes"
        :plan-periods="planPeriods"
        :selected-plan-types="filters.plan_type"
        :selected-plan-periods="filters.plan_period"
        @input-types="filters.plan_type = $event"
        @input-periods="filters.plan_period = $event"
      />
      <oms-filter-statuses :title="t('features.vue.orders.table.status')" :statuses="statuses" v-model="filters.status" />
      <oms-filter-statuses
        :title="t('features.vue.orders.filters.shipment_status')"
        :statuses="shipmentStatuses"
        v-model="filters.shipment_status"
      >
        <p>
          {{ t('features.vue.orders.filters.shipment_status_description') }}
        </p>
      </oms-filter-statuses>
      <oms-filter-statuses
        :title="t('features.vue.orders.filters.shipping_method')"
        icon="ri-ship-line"
        :statuses="shippingMethods"
        v-model="filters.shipping_method_ids"
      />
      <oms-filter-statuses
        :title="t('features.vue.orders.filters.conversion_platform')"
        icon="ri-guide-line"
        :statuses="conversionPlatforms"
        v-model="filters.conversion_platform"
      />
      <oms-filter-statuses
        :title="t('features.vue.orders.filters.subscription_status')"
        icon="ri-git-branch-line"
        :statuses="subscriptionStatuses"
        v-model="filters.subscription_status"
      />
      <oms-filter-radio-buttons
        :title="t('features.vue.orders.filters.first_invoice')"
        icon="ri-refresh-line"
        :options="firstInvoiceOptions"
        v-model="filters.first_invoice"
      />
      <oms-filter-radio-buttons
        :title="t('features.vue.orders.filters.gift')"
        icon="ri-gift-line"
        :options="giftOptions"
        v-model="filters.gift"
      />
      <oms-filter-radio-buttons
        :title="t('features.vue.orders.filters.blacklisted')"
        icon="ri-forbid-line"
        :options="blacklistedOptions"
        v-model="filters.blacklisted"
      />
      <oms-filter-statuses
        :title="t('features.vue.orders.filters.risks')"
        icon="ri-alarm-warning-line"
        :statuses="orderRisks"
        v-model="filters.order_risks"
      />
      <oms-filter-statuses
        :title="t('features.vue.orders.filters.dispute')"
        icon="ri-refund-line"
        :statuses="invoiceDisputeReasons"
        v-model="filters.invoice_dispute_reasons"
      />
      <oms-filter-statuses
        :title="t('features.vue.orders.filters.source')"
        icon="ri-at-line"
        :statuses="sources"
        v-model="filters.source_id"
      />
      <oms-filter-fetch-autocomplete
        :title="t('features.vue.orders.filters.coupon')"
        icon="ri-coupon-line"
        :fetch="{ url: '/addons/search_coupon.json', responseField: 'coupons' }"
        v-model="filters.coupons"
      />
      <oms-filter-autocomplete
        :title="t('features.vue.orders.filters.country')"
        icon="ri-earth-line"
        :options="countries"
        v-model="filters.country"
      />
      <oms-filter-autocomplete
        :title="t('features.vue.orders.filters.state_code')"
        :options="stateCodes"
        v-model="filters.state_code"
      >
        <div class="alert alert-warning">{{ t('features.vue.orders.filters.state_code_warning') }}</div>
      </oms-filter-autocomplete>
      <oms-filter-radio-buttons
        :title="t('features.vue.orders.filters.has_addons')"
        icon="ri-links-fill"
        :options="hasAddonsOptions"
        v-model="filters.has_addons"
      />
      <oms-filter-fetch-autocomplete
        :title="t('features.vue.orders.filters.addons')"
        :fetch="{ url: '/addons/search.json', responseField: 'addons' }"
        v-model="filters.addons"
      />
      <oms-filter-fetch-autocomplete
        :title="t('features.vue.orders.filters.addon_groups')"
        :fetch="{ url: '/addon_groups/search.json', responseField: 'addon_groups' }"
        v-model="filters.addon_groups"
      />
      <oms-filter-tags :title="t('features.vue.orders.filters.tags')" :tags="tags" v-model="filters.tag_filters" />
      <oms-filter-date-range
        :title="t('features.vue.orders.filters.shippable_at')"
        icon="ri-map-pin-time-line"
        v-model="filters.shippable_at"
      />
      <oms-filter-date-range
        :title="t('features.vue.orders.filters.last_printed_at')"
        icon="ri-printer-line"
        v-model="filters.printed_at"
      >
        <p>
          {{ t('features.vue.orders.filters.last_printed_at_description') }}
        </p>
      </oms-filter-date-range>
      <oms-filter-date-range
        :title="t('features.vue.orders.filters.originally_printed_at')"
        icon="ri-printer-line"
        v-model="filters.original_printed_at"
      >
        <p>
          {{ t('features.vue.orders.filters.originally_printed_at_description') }}
        </p>
      </oms-filter-date-range>
      <oms-filter-date-range
        :title="t('features.vue.orders.filters.current_term_start')"
        icon="ri-calendar-event-line"
        v-model="filters.current_term_start"
      />
      <oms-filter-statuses
        :title="t('features.vue.orders.filters.shipping_invoice_status')"
        :statuses="shippingInvoiceStatuses"
        v-model="filters.shipping_invoice_status"
        color="secondary"
      />
      <oms-filter-date-range
        :title="t('features.vue.orders.filters.invoice_paid_at')"
        icon="ri-money-dollar-box-line"
        v-model="filters.invoice_paid_at"
      >
        <p>
          {{ t('features.vue.orders.filters.invoice_paid_at_description_1') }}
        </p>
        <p>
          {{ t('features.vue.orders.filters.invoice_paid_at_description_2') }}
        </p>
      </oms-filter-date-range>
      <oms-filter-date-range
        :title="t('features.vue.orders.filters.transaction_date')"
        icon="ri-calendar-check-line"
        v-model="filters.transaction_date"
      />
      <oms-filter-ids :title="t('features.vue.orders.filters.order_ids')" v-model="filters.order_ids" />
      <oms-filter-ids :title="t('features.vue.orders.filters.tracking_numbers')" icon="ri-truck-line" :placeholder="t('features.vue.orders.filters.tracking_numbers')" v-model="filters.tracking_numbers" />

      <div class="d-flex flex-wrap ml-auto">
        <div class="form-inline mr-2 mb-2">
          <input
            class="form-control"
            v-model="filters.keyword"
            @keyup.enter="submit"
            type="text"
            :placeholder="t('features.vue.orders.filters.search_placeholder')"
          />
        </div>

        <button class="btn btn-primary mr-2 mb-2" @click="submit" :disabled="isLoading || !isDirty">
          <span class="ri-search-line"></span> {{ t('features.vue.orders.filters.search_button') }}
        </button>

        <order-actions
          :filters="filters"
          :disabled="isDirty"
          :shipping-methods="shippingMethodsForPrinting"
          :hold-status-reasons="holdStatusReasons"
          :current-tags="tags"
          :addons="addons"
          :addon_groups="addon_groups"
          :statuses="statuses"
          :total-results="totalResults"
        />
      </div>
    </div>

    <div class="card-body pt-0 pb-1">
      <oms-filter-content-plans
        :title="t('features.vue.orders.filters.plan')"
        :plan-types="planTypes"
        :plan-periods="planPeriods"
        :selected-plan-types="filters.plan_type"
        :selected-plan-periods="filters.plan_period"
      />
      <oms-filter-content-statuses :title="t('features.vue.orders.table.status')" :statuses="statuses" :modelValue="filters.status" />
      <oms-filter-content-statuses
        :title="t('features.vue.orders.filters.shipment_status')"
        :statuses="shipmentStatuses"
        v-model="filters.shipment_status"
      />
      <oms-filter-content-statuses
        :title="t('features.vue.orders.filters.shipping_method')"
        :statuses="shippingMethods"
        v-model="filters.shipping_method_ids"
      />
      <oms-filter-content-statuses
        :title="t('features.vue.orders.filters.conversion_platform')"
        :statuses="conversionPlatforms"
        v-model="filters.conversion_platform"
      />
      <oms-filter-content-statuses
        :title="t('features.vue.orders.filters.subscription_status')"
        :statuses="subscriptionStatuses"
        v-model="filters.subscription_status"
      />
      <oms-filter-content-basic
        :title="t('features.vue.orders.filters.first_invoice')"
        default-value="all"
        :mapping="firstInvoiceOptions"
        :modelValue="filters.first_invoice"
      />
      <oms-filter-content-basic
        :title="t('features.vue.orders.filters.gift')"
        default-value="all"
        :mapping="giftOptions"
        :modelValue="filters.gift"
      />
      <oms-filter-content-basic
        :title="t('features.vue.orders.filters.blacklisted')"
        default-value="all"
        :mapping="blacklistedOptions"
        :modelValue="filters.blacklisted"
      />
      <oms-filter-content-statuses
        :title="t('features.vue.orders.filters.risks')"
        :statuses="orderRisks"
        v-model="filters.order_risks"
      />
      <oms-filter-content-statuses
        :title="t('features.vue.orders.filters.dispute')"
        :statuses="invoiceDisputeReasons"
        :modelValue="filters.invoice_dispute_reasons"
      />
      <oms-filter-content-statuses
        :title="t('features.vue.orders.filters.source')"
        :statuses="sources"
        :modelValue="filters.source_id"
      />
      <oms-filter-content-named-items :title="t('features.vue.orders.filters.coupon')" :modelValue="filters.coupons" />
      <oms-filter-content-statuses
        :title="t('features.vue.orders.filters.country')"
        :statuses="countries"
        :modelValue="filters.country"
      />
      <oms-filter-content-statuses
        :title="t('features.vue.orders.filters.state_code')"
        :statuses="stateCodes"
        :modelValue="filters.state_code"
      />
      <oms-filter-content-basic
        :title="t('features.vue.orders.filters.has_addons')"
        default-value="all"
        :mapping="hasAddonsOptions"
        :modelValue="filters.has_addons"
      />
      <oms-filter-content-named-items :title="t('features.vue.orders.filters.addon_groups')" :modelValue="filters.addon_groups" />
      <oms-filter-content-named-items :title="t('features.vue.orders.filters.addons')" :modelValue="filters.addons" />
      <oms-filter-content-tags :title="t('features.vue.orders.filters.tags')" :modelValue="filters.tag_filters" />
      <oms-filter-content-date-range :title="t('features.vue.orders.filters.shippable_at')" :modelValue="filters.shippable_at" />
      <oms-filter-content-date-range :title="t('features.vue.orders.filters.last_printed_at')" :modelValue="filters.printed_at" />
      <oms-filter-content-date-range
        :title="t('features.vue.orders.filters.originally_printed_at')"
        :modelValue="filters.original_printed_at"
      />
      <oms-filter-content-date-range
        :title="t('features.vue.orders.filters.current_term_start')"
        :modelValue="filters.current_term_start"
      />
      <oms-filter-content-date-range
        :title="t('features.vue.orders.filters.invoice_paid_at')"
        :modelValue="filters.invoice_paid_at"
      />
      <oms-filter-content-date-range
        :title="t('features.vue.orders.filters.transaction_date')"
        :modelValue="filters.transaction_date"
      />
    </div>
  </div>
</template>

<script>
import selectedOrderIds from 'javascripts/stores/orders/selected-ids'
import buildQueryString from '../../../helpers/query-builder'
import OmsFilterContentBasic from '../filter-content/basic.vue'
import OmsFilterContentDateRange from '../filter-content/date-range.vue'
import OmsFilterContentNamedItems from '../filter-content/named-items.vue'
import OmsFilterContentPlans from '../filter-content/plans.vue'
import OmsFilterContentStatuses from '../filter-content/statuses.vue'
import OmsFilterContentTags from '../filter-content/tags.vue'
import OmsFilterAutocomplete from '../filter/autocomplete.vue'
import OmsFilterDateRange from '../filter/date-range.vue'
import OmsFilterFetchAutocomplete from '../filter/fetch-autocomplete.vue'
import OmsFilterIds from '../filter/ids.vue'
import OmsFilterPlans from '../filter/plans.vue'
import OmsFilterRadioButtons from '../filter/radio-buttons.vue'
import OmsFilterStatuses from '../filter/statuses.vue'
import OmsFilterTags from '../filter/tags.vue'
import OrderActions from '../order-actions.vue'

import {mapActions, mapState} from 'pinia'
import {useOrdersStore} from '../../stores/orders'

export default {
  name: 'order-filters',
  inject: ['i18n'],
  props: {
    initialData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    // Fix reactivity by setting default values for all filters
    this.initialData.filters = {
      plan_type: {},
      plan_period: {},
      status: {},
      shipment_status: {},
      shipping_method_ids: {},
      conversion_platform: {},
      subscription_status: {},
      first_invoice: null,
      gift: null,
      blacklisted: null,
      order_risks: {},
      invoice_dispute_reasons: {},
      source_id: {},
      coupons: {},
      country: {},
      state_code: {},
      has_addons: null,
      addon_groups: {},
      addons: {},
      tag_filters: {},
      shippable_at: null,
      printed_at: null,
      original_printed_at: null,
      current_term_start: null,
      shipping_invoice_status: {},
      invoice_paid_at: null,
      transaction_date: null,
      order_ids: {},
      tracking_numbers: {},
      keyword: '',
      ...this.initialData.filters
    }

    return {
      ...this.initialData,
      isDirty: false
    }
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.isDirty = true
      }
    }
  },

  computed: {
    ...mapState(useOrdersStore, {
      isLoading: 'isLoading',
      totalResults: 'getTotalResults'
    }),

    searchUrl() {
      return `${this.baseUrl}?${this.filterQueryString}`
    },

    firstInvoiceOptions() {
      return [
        {id: 'all', name: this.t('features.vue.orders.filters.all')},
        {id: '1', name: this.t('features.vue.orders.filters.first_invoice_option')},
        {id: '0', name: this.t('features.vue.orders.filters.renewal')}
      ]
    },

    giftOptions() {
      return [
        {id: 'all', name: this.t('features.vue.orders.filters.all')},
        {id: '1', name: this.t('features.vue.orders.filters.gift_option')},
        {id: '0', name: this.t('features.vue.orders.filters.non_gift')}
      ]
    },

    blacklistedOptions() {
      return [
        {id: 'all', name: this.t('features.vue.orders.filters.all')},
        {id: '1', name: this.t('features.vue.orders.filters.blacklisted_option')},
        {id: '0', name: this.t('features.vue.orders.filters.not_blacklisted')}
      ]
    },

    hasAddonsOptions() {
      return [
        {id: 'all', name: this.t('features.vue.orders.filters.all')},
        {id: '1', name: this.t('features.vue.orders.filters.addons_option')},
        {id: '0', name: this.t('features.vue.orders.filters.no_addons')}
      ]
    },

    filterQueryString() {
      return buildQueryString(this.filters, 'q')
    }
  },

  methods: {
    ...mapActions(useOrdersStore, ['fetchOrders']),

    t(key, options) {
      return this.i18n ? this.i18n.t(key, options) : key
    },

    submit() {
      this.isDirty = false

      this.fetchOrders(this.filterQueryString).then(() => {
        window.history.pushState('', '', this.searchUrl.replace('.json', ''))
        this.refresh()
      })
    },

    refresh() {
      selectedOrderIds.toArray().forEach((id) => {
        document
          .querySelectorAll('tr[data-id=' + id + '] > td:first-child input[type=checkbox]')
          .forEach((element) => {
            element.checked = true
          })
      })

      $.onmount()
    }
  },

  components: {
    OmsFilterPlans,
    OmsFilterAutocomplete,
    OmsFilterStatuses,
    OmsFilterRadioButtons,
    OmsFilterFetchAutocomplete,
    OmsFilterTags,
    OmsFilterDateRange,
    OmsFilterIds,
    OrderActions,
    OmsFilterContentPlans,
    OmsFilterContentBasic,
    OmsFilterContentNamedItems,
    OmsFilterContentTags,
    OmsFilterContentDateRange,
    OmsFilterContentStatuses
  }
}
</script>

<style scoped>
/* Staging look: flex-1 filter buttons grow to fill each row. The shared filter-dropdown sets
   min-width:0 (PR #3530, for the scrollable dropdown menu), which lets flex-1 shrink to icon-only
   when a page has many filters (orders has ~28). Restore min-width:auto here so each button floors
   at its label width and rows fill edge-to-edge. Scoped to orders -- other pages keep master. */
.card-body :deep(.flex-1) {
  min-width: auto;
}
</style>
